import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DashboardDeviceLabel } from 'App/dashboards/_components/DashboardDeviceLabel'
import { RootZoneSparkline } from 'App/dashboards/_components/RootZoneSparkline'
import { SoilChartModal } from 'App/dashboards/_components/SoilChartModal'
import { SoilDeficitChip } from 'App/dashboards/_components/SoilDeficitChip'
import { WidgetCard } from 'App/dashboards/_components/WidgetCard'
import { downloadCsv } from 'App/dashboards/_utils/dashboardDownloads'
import { useDashboardHeader } from 'App/dashboards/_utils/dashboardHeaderContext'
import { getRootZoneAverage } from 'App/dashboards/_utils/getRootZoneAverage'
import { useHorizontalScrollEdges } from 'App/dashboards/_utils/useHorizontalScrollEdges'
import type {
  TWaterMonitoringProbe,
  TWaterMonitoringSection,
  TWaterMonitoringZone,
} from 'App/dashboards/_utils/useWaterMonitoring'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { colors, dashboardColors } from 'settings/colors'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import type { TUnitConverterTypicalReturn } from 'utils/unitConverter/types'
import { unitConverter } from 'utils/unitConverter/unitConverter'

export type TWaterMonitoringTableZone = TWaterMonitoringZone & { cropName: string | null }

// One section per property, each carrying its soil-moisture access (access is per-property).
// Soil access is per-property and gates the SOIL COLUMNS, not the section: a user with irrigation
// access but no VIEW_SOIL_MOISTURE_DETAILS still owns the zone data (Applied/Planned, Rain, ETc).
export type TWaterMonitoringTableSection = Omit<TWaterMonitoringSection, 'zones'> & {
  hasSoilAccess: boolean
  showCrops: boolean
  zones: TWaterMonitoringTableZone[]
}

const blank = '' // off-type cell (soil column on a zone row, or zone column on a probe row)
// Rain and ETc cells of a property whose planning-data is still loading, so they don't read as no data.
const pendingCell = <span css={{ color: dashboardColors.mutedText }}>{'…'}</span>
const dash = (s: string): string => s || '-'

const formatVolume = (mm: number | null): string =>
  mm === null ? '' : unitConverter.irrigationActivity(mm).valueWithSuffix()

const formatDuration = (ms: number | null): string =>
  ms === null ? '' : minutesToHoursAndMinutes(ms / 60000)

const formatRain = (mm: number | null): string =>
  mm === null ? '' : unitConverter.precipitation(mm).valueWithSuffix()

const formatEtc = (mm: number | null): string =>
  mm === null ? '' : unitConverter.evapotranspiration(mm).valueWithSuffix()

const formatPercent = (value: number | null): string =>
  value === null ? '' : `${Math.round(value * 10) / 10}%`

// Render a measured value with its unit muted (e.g. "0.8" + grey "in"), matching the design's two-tone
// cells. CSV keeps the combined string (format* above); this is display-only.
const renderValueWithUnit = (
  convert: (value: number) => TUnitConverterTypicalReturn,
  mm: number | null,
): ReactNode => {
  if (mm === null) return '-'

  const converted = convert(mm)

  return (
    <>
      {converted.valueAsString()}
      <span css={{ color: dashboardColors.mutedText }}>{` ${converted.suffix()}`}</span>
    </>
  )
}

// A stacked column header: metric name over its period. The unit is not here — it sits in its own
// header row below, so that Applied/Planned Water can name themselves once across the two columns
// (duration and volume) they cover, as the design has it.
const ColumnHeader = ({ label, period }: { label: string; period?: string }) => (
  <div css={{ display: 'flex', flexDirection: 'column' }}>
    <span css={{ fontWeight: 600, color: dashboardColors.columnHeaderText }}>{label}</span>
    {period && <span css={{ fontWeight: 500, color: dashboardColors.subtleText }}>{period}</span>}
  </div>
)

type TColumn = {
  key: string
  group: 'label' | 'soil' | 'zone'
  // Header parts kept as data rather than a ready-made node: adjacent columns that share a label and
  // period are rendered under one spanning cell, which needs to compare them.
  headerLabel: string
  headerPeriod?: string
  headerUnit?: string
  // Renderers per row type; the off-type renderer returns blank so the cell is empty.
  zone: (zone: TWaterMonitoringZone) => ReactNode
  probe: (probe: TWaterMonitoringProbe) => ReactNode
  // Plain-text values for the CSV export (off-type returns '').
  csvZone: (zone: TWaterMonitoringZone) => string
  csvProbe: (probe: TWaterMonitoringProbe) => string
  // Column name used in the CSV export. Omitted for the label column, which the export heads with
  // "Zone/Probe" instead.
  csvHeader?: string
}

// Built at render (not module load) so the headers resolve in the current locale, consistent with the
// other dashboard widgets and the registry's getTitle.
const buildColumns = (onProbeChartOpen: (probe: TWaterMonitoringProbe) => void): TColumn[] => {
  const past = translate.phrases.banyanApp('Past 7 Days')
  // Probe Uptime spans 30 days while every other windowed column is 7 — label it explicitly so the
  // two periods aren't read as one.
  const past30 = translate.phrases.banyanApp('Past 30 Days')
  const next = translate.phrases.banyanApp('Next 7 Days')
  // Header units come from the same unit-symbol source the values do, so they read lowercase — the
  // convention everywhere else in the app, where headings render "Volume (mm)" / "Label (in)".
  const hr = translate.measurements.hours.unit()
  // Volume follows the user's setting (in / mm) rather than being hard-coded.
  const volumeUnit = unitConverter.irrigationActivity().suffix()
  const applied = translate.phrases.banyanApp('Applied Water')
  const planned = translate.phrases.banyanApp('Planned Water')
  const rain = translate.phrases.banyanApp('Rain')
  const etc = translate.phrases.banyanApp('ETc')

  return [
    {
      key: 'label',
      group: 'label',
      headerLabel: translate.phrases.banyanApp('Zone/Probe'),
      zone: (z) => <span css={{ fontWeight: 600 }}>{z.zoneName}</span>,
      probe: (p) =>
        p.externalVendorNames?.length ? (
          <span css={{ display: 'block', paddingLeft: 16, fontWeight: 400 }}>
            <DashboardDeviceLabel name={p.name} externalVendorNames={p.externalVendorNames} />
          </span>
        ) : (
          <span css={{ paddingLeft: 16, fontWeight: 400 }}>{p.name}</span>
        ),
      csvZone: (z) => z.zoneName,
      csvProbe: (p) => p.name,
    },
    {
      key: 'rootZoneAvg',
      group: 'soil',
      headerLabel: translate.phrases.banyanApp('Root Zone Avg'),
      headerPeriod: past,
      zone: () => blank,
      probe: (p) => {
        const sparkline = <RootZoneSparkline values={p.rootZoneSeries} madValue={p.madValue ?? undefined} />

        // Clickable → expand to a larger chart, but only when there's a series to show.
        return p.rootZoneSeries.some((v) => v !== null) ? (
          <button
            type="button"
            onClick={() => onProbeChartOpen(p)}
            aria-label={translate.phrases.banyanApp('Open Root Zone chart for {{probeName}}', {
              probeName: p.name,
            })}
            css={{
              'background': 'none',
              'border': 'none',
              'padding': 0,
              'cursor': 'pointer',
              'display': 'block',
              '&:hover': { opacity: 0.85 },
            }}
          >
            {sparkline}
          </button>
        ) : (
          sparkline
        )
      },
      csvZone: () => blank,
      // The sparkline doesn't fit a cell; export the window average (distinct from Current = latest).
      csvProbe: (p) => formatPercent(getRootZoneAverage(p.rootZoneSeries)),
      csvHeader: translate.phrases.banyanApp('Root Zone Avg'),
    },
    {
      key: 'current',
      group: 'soil',
      headerLabel: translate.phrases.banyanApp('Current'),
      zone: () => blank,
      probe: (p) => <SoilDeficitChip value={p.currentValue} madValue={p.madValue ?? undefined} />,
      csvZone: () => blank,
      csvProbe: (p) => formatPercent(p.currentValue),
      csvHeader: translate.phrases.banyanApp('Current'),
    },
    {
      key: 'probeUptime',
      group: 'soil',
      headerLabel: translate.phrases.banyanApp('Probe Uptime'),
      headerPeriod: past30,
      zone: () => blank,
      // % of the past 30 days' hours the probe reported, computed in BigQuery by /soil/probe-uptime.
      probe: (p) => dash(formatPercent(p.uptimePercent)),
      csvZone: () => blank,
      csvProbe: (p) => formatPercent(p.uptimePercent),
      csvHeader: translate.phrases.banyanApp('Probe Uptime'),
    },
    {
      key: 'appliedWaterHr',
      group: 'zone',
      headerLabel: applied,
      headerPeriod: past,
      headerUnit: hr,
      zone: (z) => dash(formatDuration(z.appliedDurationMs)),
      probe: () => blank,
      csvZone: (z) => formatDuration(z.appliedDurationMs),
      csvProbe: () => blank,
      csvHeader: `${applied} (${hr})`,
    },
    {
      key: 'appliedWaterIn',
      group: 'zone',
      headerLabel: applied,
      headerPeriod: past,
      headerUnit: volumeUnit,
      zone: (z) => renderValueWithUnit(unitConverter.irrigationActivity, z.appliedVolumeMm),
      probe: () => blank,
      csvZone: (z) => formatVolume(z.appliedVolumeMm),
      csvProbe: () => blank,
      csvHeader: `${applied} (${volumeUnit})`,
    },
    {
      key: 'plannedWaterHr',
      group: 'zone',
      headerLabel: planned,
      headerPeriod: next,
      headerUnit: hr,
      zone: (z) => dash(formatDuration(z.plannedDurationMs)),
      probe: () => blank,
      csvZone: (z) => formatDuration(z.plannedDurationMs),
      csvProbe: () => blank,
      csvHeader: `${planned} (${hr})`,
    },
    {
      key: 'plannedWaterIn',
      group: 'zone',
      headerLabel: planned,
      headerPeriod: next,
      headerUnit: volumeUnit,
      zone: (z) => renderValueWithUnit(unitConverter.irrigationActivity, z.plannedVolumeMm),
      probe: () => blank,
      csvZone: (z) => formatVolume(z.plannedVolumeMm),
      csvProbe: () => blank,
      csvHeader: `${planned} (${volumeUnit})`,
    },
    {
      key: 'rainPast',
      group: 'zone',
      headerLabel: rain,
      headerPeriod: past,
      zone: (z) =>
        z.planningPending ? pendingCell : renderValueWithUnit(unitConverter.precipitation, z.rainPastMm),
      probe: () => blank,
      csvZone: (z) => formatRain(z.rainPastMm),
      csvProbe: () => blank,
      csvHeader: `${rain} (${past})`,
    },
    {
      key: 'rainNext',
      group: 'zone',
      headerLabel: rain,
      headerPeriod: next,
      zone: (z) =>
        z.planningPending ? pendingCell : renderValueWithUnit(unitConverter.precipitation, z.rainNextMm),
      probe: () => blank,
      csvZone: (z) => formatRain(z.rainNextMm),
      csvProbe: () => blank,
      csvHeader: `${rain} (${next})`,
    },
    {
      key: 'etcPast',
      group: 'zone',
      headerLabel: etc,
      headerPeriod: past,
      zone: (z) =>
        z.planningPending ? pendingCell : renderValueWithUnit(unitConverter.evapotranspiration, z.etcPastMm),
      probe: () => blank,
      csvZone: (z) => formatEtc(z.etcPastMm),
      csvProbe: () => blank,
      csvHeader: `${etc} (${past})`,
    },
    {
      key: 'etcNext',
      group: 'zone',
      headerLabel: etc,
      headerPeriod: next,
      zone: (z) =>
        z.planningPending ? pendingCell : renderValueWithUnit(unitConverter.evapotranspiration, z.etcNextMm),
      probe: () => blank,
      csvZone: (z) => formatEtc(z.etcNextMm),
      csvProbe: () => blank,
      csvHeader: `${etc} (${next})`,
    },
  ]
}

// Fixed column widths (px) keyed by column. A fixed table layout keeps widths stable regardless of
// content — without it, collapsing a property's rows lets columns shrink to their header text.
const COLUMN_WIDTHS: Record<string, number> = {
  label: 180,
  rootZoneAvg: 185,
  current: 100,
  probeUptime: 120,
  // Applied/Planned each split into two narrow sub-columns under one heading. Kept tight so the
  // volume half sits close to the heading rather than drifting off to the right — the design pairs
  // them at 150 total; 80 each buys room for three-digit durations ("120h 30m") without clipping.
  appliedWaterHr: 80,
  appliedWaterIn: 80,
  plannedWaterHr: 80,
  plannedWaterIn: 80,
  rainPast: 120,
  rainNext: 120,
  etcPast: 120,
  etcNext: 120,
}

const colWidth = (key: string): number => COLUMN_WIDTHS[key] ?? 120
// Rules on pinned cells are drawn as inset shadows, not borders. Under `border-collapse: collapse` a
// border belongs to the table rather than the cell, so it stays put while the cell pins — leaving a
// 1px transparent line with rows visibly scrolling through it. A shadow is painted by the cell and
// travels with it. Cells carrying both rules compose them.
const HEADER_BOTTOM_RULE = `inset 0 -1px 0 ${colors.grey200}`
const LABEL_RIGHT_RULE = `inset -1px 0 0 ${colors.grey200}`

const headerCellCss = {
  textAlign: 'left' as const,
  padding: '10px 8px',
  fontSize: 14,
  fontWeight: 600,
  verticalAlign: 'top' as const,
  boxShadow: HEADER_BOTTOM_RULE,
}

// Group band ("SOIL MOISTURE PROBE DATA" / "ZONE DATA"): uppercase, near-black foreground, semibold.
// Per design the Soil band is grey (accent) and the Zone band is light blue (information-subtle).
// Only the band carries the soil tint — the column-name and unit rows beneath it are white, matching
// the card, while the Zone group stays tinted all the way down.
const groupBandCss = {
  ...headerCellCss,
  color: colors.midnight,
  textTransform: 'uppercase' as const,
}

const bodyCellCss = {
  textAlign: 'left' as const,
  padding: '8px',
  fontSize: 14,
  // Design: numeric values are medium (500); the label column overrides per row (zone 600 / probe 400).
  fontWeight: 500,
  color: colors.midnight,
  borderTop: `1px solid ${colors.grey200}`,
  verticalAlign: 'middle' as const,
  // Fixed layout means content can't widen the column — clip overflow with an ellipsis. Data cells
  // are short values; the label column opts out of this (see labelCellCss) so names stay readable.
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// The Zone/Probe (label) column: a vertical divider from the data columns + the design's wider (14px)
// horizontal padding (data cells use 8px). Zone and probe names show in full and wrap onto further
// lines rather than truncating — a truncated probe name is often indistinguishable from its
// neighbours, which defeats the point of the column.
const labelCellCss = {
  boxShadow: LABEL_RIGHT_RULE,
  paddingLeft: 14,
  paddingRight: 14,
  whiteSpace: 'normal' as const,
  overflow: 'visible' as const,
  textOverflow: 'clip' as const,
  overflowWrap: 'anywhere' as const,
}

// The label column stays put while the data columns scroll under it. It needs an opaque background of
// its own: a sticky cell paints over the scrolled content, and anything transparent would let that
// content show through.
// The table scrolls itself rather than letting the page scroll it. A horizontally scrollable element
// is a scroll container on BOTH axes (per spec, `visible` computes to `auto` when the other axis
// isn't), so a sticky header inside one anchors to that container, not the viewport — with the
// container sized to its content that pins nothing. Bounding the height gives the header something
// real to stick to.
//
// The page header's height is read from the custom property the shell publishes, so the table grows
// as the header compacts instead of leaving a gap. What's left is static chrome: shell padding, card
// padding and borders. There's no card header to allow for — this dashboard hoists its download into
// the page header and renders the card without one.
// Matches the shell's own threshold, so the header compacts at the same moment the table reports it.
const COMPACT_SCROLL_THRESHOLD = 24
// Breathing room above each property section, per the design. Rendered as a spacer row rather than
// padding on the banner: the banner is sticky, so padding would travel with it and open a gap under
// the header once pinned. A row scrolls away as it should.
const PROPERTY_SECTION_GAP = 20
// Layers within the table's own stacking context, low to high: the fade under everything; the sticky
// label column over the data cells it slides across; property banners over the fade; the pinned
// header over the banners that scroll up into it; and the label's header cell over the lot, being
// pinned on both axes. Everything that overlaps something else needs its own level — at equal
// z-index the later element wins on DOM order, which is how the banners ended up over the header.
const FADE_Z = 1
const STICKY_LABEL_Z = 2
const PROPERTY_BANNER_Z = 3
const STICKY_HEADER_Z = 4
const STICKY_HEADER_CORNER_Z = 5

// `showEdge` hangs the scroll indicator off the column's own right edge as a pseudo-element, rather
// than floating an overlay band beside it. It therefore reads identically beside the header, the zone
// rows and the probe rows, and it tints nothing — an overlay would have to paint across the very text
// it sits over. Same gradient and width as the right-hand fade, so the two edges match.
const stickyLabelCss = (background: string, showEdge: boolean) => ({
  'position': 'sticky' as const,
  'left': 0,
  'zIndex': STICKY_LABEL_Z,
  background,
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    left: '100%',
    width: SCROLL_FADE_WIDTH,
    pointerEvents: 'none' as const,
    background: scrollFadeGradient('left'),
    opacity: showEdge ? 1 : 0,
    transition: '200ms ease all',
  },
})

// Lifts a cell above the scroll fades. The fades mark scrollable *data*, so veiling the header or a
// property's banner just reads as a rendering fault — worst on the property name, which is long
// enough to run well past the sticky column. SummaryGridTable does the same by clipping its scroll
// shadow to start below the header.
// Header cells pin to the top of the table's scroll viewport, each row below the one above it.
const stickyHeaderCss = (top: number) => ({
  position: 'sticky' as const,
  top,
  zIndex: STICKY_HEADER_Z,
})

// Width and gradient of the scroll-edge indicators, shared by the sticky column's trailing edge and
// the right-hand overlay so both sides fade at the same rate over the same distance.
const SCROLL_FADE_WIDTH = 24

const scrollFadeGradient = (side: 'left' | 'right'): string =>
  `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${dashboardColors.scrollFade}, transparent)`

// The right-hand fade has no sticky column to hang off, so it stays an overlay pinned to the edge.
const scrollFadeCss = (side: 'left' | 'right', offset: number) => ({
  position: 'absolute' as const,
  top: 0,
  bottom: 0,
  zIndex: FADE_Z,
  [side]: offset,
  width: SCROLL_FADE_WIDTH,
  pointerEvents: 'none' as const,
  background: scrollFadeGradient(side),
  transition: '200ms ease all',
})

const centeredMessageCss = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center' as const,
  minHeight: 80,
  padding: 16,
  color: colors.grey800,
  fontSize: 14,
}

// A zone row (zone-data cells filled, soil cells blank) followed by its probe rows (soil cells filled,
// zone cells blank).
const ZoneRows = ({
  zone,
  columns,
  hasSoilAccess,
  showStickyEdge,
}: {
  zone: TWaterMonitoringZone
  columns: TColumn[]
  hasSoilAccess: boolean
  showStickyEdge: boolean
}) => (
  <>
    <tr css={{ background: dashboardColors.zoneColumnHeaderBg }}>
      {columns.map((column) => (
        <td
          key={column.key}
          css={{
            ...bodyCellCss,
            ...(column.group === 'label'
              ? { ...labelCellCss, ...stickyLabelCss(dashboardColors.zoneColumnHeaderBg, showStickyEdge) }
              : {}),
          }}
        >
          {column.zone(zone)}
        </td>
      ))}
    </tr>
    {/* Probe rows carry every soil value (the soil columns are blank on zone rows), so withholding
        them is what gates soil data — no per-cell blanking needed. The header is shared across all
        properties, so the columns themselves can't be hidden for one property's section. */}
    {hasSoilAccess &&
      zone.probes.map((probe) => (
        <tr key={probe.lngLat}>
          {columns.map((column) => (
            <td
              key={column.key}
              css={{
                ...bodyCellCss,
                ...(column.group === 'label'
                  ? { ...labelCellCss, ...stickyLabelCss(colors.white, showStickyEdge) }
                  : {}),
              }}
            >
              {column.probe(probe)}
            </td>
          ))}
        </tr>
      ))}
  </>
)

// The cell's overflow must stay visible or the sticky label can't pin to the scroll container's edge.
const CropRow = ({ cropName, colSpan }: { cropName: string | null; colSpan: number }) => (
  <tr>
    <td
      colSpan={colSpan}
      css={{
        ...bodyCellCss,
        overflow: 'visible',
        padding: '8px 14px',
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: dashboardColors.columnHeaderText,
      }}
    >
      <span css={{ position: 'sticky', left: 14 }}>
        {cropName ?? translate.phrases.banyanApp('Unknown Crop')}
      </span>
    </td>
  </tr>
)

const messageRowCss = {
  textAlign: 'center' as const,
  padding: 16,
  color: colors.grey800,
  fontSize: 14,
}

// One property as a <tbody> within the shared table: a full-width collapsible accordion header row,
// then either its interleaved zone/probe rows or a single full-width message row. The group band +
// column header are shared (rendered once by WaterMonitoringTable), so properties nest beneath them —
// matching the design. Per-property message precedence: fetch error → no data.
const PropertyBody = ({
  section,
  columns,
  error,
  showStickyEdge,
  stickyTop,
  isFirst,
}: {
  section: TWaterMonitoringTableSection
  columns: TColumn[]
  error?: Error | null
  showStickyEdge: boolean
  // Where the header block ends, so this property's banner pins directly beneath it.
  stickyTop: number
  // The first section sits directly under the header; the rest are separated from the property above.
  isFirst: boolean
}) => {
  // Sections with nothing to show are collapsed: they contribute only a message, so leaving them
  // expanded pushes the properties that do have data off-screen. Derived from the current zones, not
  // captured at mount, because a section can mount before its zones reach the store. `userOpen` records
  // an explicit click, which then wins. A fetch error stays out of the default so a failed load leaves
  // each section open on its message.
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const open = userOpen ?? section.zones.length > 0

  const message = error
    ? translate.phrases.banyanApp('Unable to load data. Try refreshing.')
    : translate.phrases.banyanApp('No data to display')

  const showRows = !error && section.zones.length > 0

  return (
    <tbody>
      {!isFirst && (
        <tr aria-hidden>
          {/* Carries the rule that closes the previous property. Body cells draw their line as a
              borderTop, so without one here the last row of a section would end unruled. */}
          <td
            colSpan={columns.length}
            css={{
              height: PROPERTY_SECTION_GAP,
              padding: 0,
              borderTop: `1px solid ${colors.grey200}`,
            }}
          />
        </tr>
      )}
      <tr>
        {/* Pins under the column headers while you scroll this property's rows, then gets pushed up
            by the next property's banner — so the name of whatever you're reading stays on screen. */}
        <td
          colSpan={columns.length}
          css={{ padding: 0, position: 'sticky', top: stickyTop, zIndex: PROPERTY_BANNER_Z }}
        >
          <button
            onClick={() => setUserOpen(!open)}
            aria-expanded={open}
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              background: colors.grey50,
              border: 'none',
              borderTop: `1px solid ${colors.grey200}`,
              borderBottom: `1px solid ${colors.grey200}`,
              padding: '10px 15px',
              cursor: 'pointer',
              textAlign: 'left',
              color: colors.midnight,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            {/* The banner spans every column, so its label would scroll out of view with them. Pin
                the label to the scroll container's left edge instead — the row still scrolls, but the
                property it belongs to stays readable. */}
            <span
              css={{
                position: 'sticky',
                left: 15,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
              {section.propertyName}
            </span>
          </button>
        </td>
      </tr>
      {open &&
        showRows &&
        section.zones.map((zone, index) => (
          <Fragment key={zone.zoneId}>
            {section.showCrops && (index === 0 || zone.cropName !== section.zones[index - 1].cropName) && (
              <CropRow cropName={zone.cropName} colSpan={columns.length} />
            )}
            <ZoneRows
              zone={zone}
              columns={columns}
              hasSoilAccess={section.hasSoilAccess}
              showStickyEdge={showStickyEdge}
            />
          </Fragment>
        ))}
      {open && !showRows && (
        <tr>
          <td colSpan={columns.length} css={messageRowCss}>
            {message}
          </td>
        </tr>
      )}
    </tbody>
  )
}

// Water Monitoring's dedicated table: one collapsible section per selected property, a two-level
// header (Soil Moisture Probe Data / Zone Data), and interleaved zone + probe rows. Always renders a
// section per property; per-section message covers no-access / error / no-data (access is per-property).
export const WaterMonitoringTable = ({
  sections,
  loading,
  planningLoading,
  error,
}: {
  sections: TWaterMonitoringTableSection[]
  loading: boolean
  // Rain and ETc are still arriving for some property. The table shows meanwhile, but the export waits so it
  // can't hand out a file with those columns half filled.
  planningLoading: boolean
  error?: Error | null
}) => {
  // The header and the scroll container only exist once there is at least one section, and sections
  // arrive a render late while the field-asset store hydrates. Effects that measure them must key on
  // this, or they run once against nothing and never re-run.
  const hasSections = sections.length > 0
  // Each header row pins below the one above it, so the offsets are cumulative row heights. Measured
  // rather than hardcoded: the rows wrap differently by width, locale and unit label, and a stale
  // constant would leave a gap or overlap instead of a seam.
  const bandRowRef = useRef<HTMLTableRowElement>(null)
  const nameRowRef = useRef<HTMLTableRowElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [headerOffsets, setHeaderOffsets] = useState({ names: 0, units: 0, body: 0 })

  useLayoutEffect(() => {
    const measure = () => {
      const names = bandRowRef.current?.offsetHeight ?? 0
      const units = names + (nameRowRef.current?.offsetHeight ?? 0)
      // Where the header block ends — the property banners pin here, directly under it.
      const body = theadRef.current?.offsetHeight ?? 0

      setHeaderOffsets((previous) =>
        previous.names === names && previous.units === units && previous.body === body
          ? previous
          : { names, units, body },
      )
    }

    measure()

    const observer = new ResizeObserver(measure)

    if (bandRowRef.current) observer.observe(bandRowRef.current)

    if (nameRowRef.current) observer.observe(nameRowRef.current)

    if (theadRef.current) observer.observe(theadRef.current)

    return () => observer.disconnect()
  }, [hasSections])

  const [chartProbe, setChartProbe] = useState<TWaterMonitoringProbe | null>(null)
  const openChart = useCallback((probe: TWaterMonitoringProbe) => setChartProbe(probe), [])
  // unitConverter resolves the user's unit setting through getState(), i.e. a snapshot. Subscribing
  // here is what makes the header track it: the cells convert on every render, but `columns` is
  // memoised, so without this the header keeps whichever unit was current when the table mounted —
  // and on a cold store that default is METRIC, so it would read "Mm" over inch values.
  const rainUnit = userDetailsStore.useSelector((s) => s.rain)
  const columns = useMemo(() => buildColumns(openChart), [openChart, rainUnit])
  // Every column always renders — there is no show/hide control, so the table body and the CSV
  // can't disagree about which columns exist.
  const displayedColumns = columns
  const { ref: scrollRef, element: scrollElement, atLeft, atRight } = useHorizontalScrollEdges()
  // Tell the shell when the data has been scrolled, so its header can compact. The page barely
  // scrolls now that the table scrolls itself, so this is the only signal available.
  const dashboardHeader = useDashboardHeader()
  const setHeaderScrolled = dashboardHeader?.setScrolled

  useEffect(() => {
    const element = scrollElement

    if (!setHeaderScrolled || !element) return

    // Real hysteresis: compact past the threshold, then stay compact until you're back at the very
    // top. Compacting lengthens this viewport, which can pull scrollTop back under the threshold —
    // with a single boundary that would expand the header, re-shorten the viewport, and chatter.
    let compact = false

    const onScroll = () => {
      const next = compact ? element.scrollTop > 0 : element.scrollTop > COMPACT_SCROLL_THRESHOLD

      if (next === compact) return

      compact = next

      setHeaderScrolled(next)
    }

    onScroll()

    element.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      element.removeEventListener('scroll', onScroll)

      setHeaderScrolled(false)
    }
  }, [scrollElement, setHeaderScrolled])

  const hasRows = sections.some((s) => s.zones.length > 0)

  // Empty properties sink to the bottom so the ones worth reading are at the top. Stable within each
  // group, so the caller's ordering (the property picker's) still decides the rest.
  const orderedSections = useMemo(() => {
    const withData = sections.filter((s) => s.zones.length > 0)
    const withoutData = sections.filter((s) => s.zones.length === 0)

    return [...withData, ...withoutData]
  }, [sections])

  const soilSpan = displayedColumns.filter((c) => c.group === 'soil').length
  const zoneSpan = displayedColumns.filter((c) => c.group === 'zone').length
  // Zone/Probe is one tall cell spanning both header rows; the rest fill the column-name row.
  const labelColumn = displayedColumns.find((c) => c.group === 'label')
  const dataColumns = displayedColumns.filter((c) => c.group !== 'label')

  // Adjacent columns naming the same measure over the same period share one header cell — Applied
  // Water covers its duration and volume columns, so the name and period are written once and only
  // the units differ beneath. Rain and ETc share a label but not a period, so they stay separate.
  const headerGroups = dataColumns.reduce<{ columns: TColumn[]; hasUnits: boolean }[]>((groups, column) => {
    const previous = groups[groups.length - 1]
    const first = previous?.columns[0]

    if (first && first.headerLabel === column.headerLabel && first.headerPeriod === column.headerPeriod) {
      previous.columns.push(column)

      previous.hasUnits = previous.hasUnits || Boolean(column.headerUnit)

      return groups
    }

    groups.push({ columns: [column], hasUnits: Boolean(column.headerUnit) })

    return groups
  }, [])

  const handleDownload = useCallback(() => {
    const showCropColumn = orderedSections.some((section) => section.showCrops)

    const headers = [
      translate.phrases.banyanApp('Property'),
      ...(showCropColumn ? [translate.phrases.banyanApp('Crop')] : []),
      ...displayedColumns.map((c) => c.csvHeader ?? translate.phrases.banyanApp('Zone/Probe')),
    ]

    // One row per zone (zone-data cells) then one per probe (soil cells), prefixed with the property.
    // Probe rows follow the same soil gate as the screen, and the sections keep the on-screen order,
    // so "what you can export" == "what you can see" — a section without soil access exports its zone
    // rows and no probe rows.
    const rows = orderedSections.flatMap((section) =>
      section.zones.flatMap((zone) => {
        const leading = [section.propertyName, ...(showCropColumn ? [zone.cropName ?? ''] : [])]

        return [
          [...leading, ...displayedColumns.map((c) => c.csvZone(zone))],
          ...(section.hasSoilAccess
            ? zone.probes.map((probe) => [...leading, ...displayedColumns.map((c) => c.csvProbe(probe))])
            : []),
        ]
      }),
    )

    const today = translate.dates.format(moment.tz(moment.tz.guess()), 'MMM D, YYYY')

    downloadCsv(`${translate.phrases.banyanApp('Water Monitoring')} - ${today}.csv`, headers, rows)
  }, [displayedColumns, orderedSections])

  return (
    <WidgetCard
      title={translate.phrases.banyanApp('Water Monitoring')}
      loading={loading}
      fillHeight
      onDownload={hasRows && !loading && !planningLoading && !error ? handleDownload : undefined}
    >
      {hasSections ? (
        <div
          css={{
            position: 'relative',
            // Fills the card's remaining height, so the scroller below gets an exact size from the
            // layout rather than a computed one — the page header, card header, paddings and the
            // column's flex gap are all accounted for by flex itself.
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            // Own stacking context, so the sticky column / fade / header ordering below stays a
            // private matter. Without it those z-indexes compete with WidgetCard's loading overlay
            // in the same context and punch holes through it.
            zIndex: 0,
            border: `1px solid ${colors.grey200}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            ref={scrollRef}
            css={{
              overflow: 'auto',
              // Exactly the space flex gives it, with no floor. A min-height here fights the parent:
              // on a short viewport it forces the scroller taller than its container, and the card's
              // overflow:hidden silently clips the difference rather than scrolling it.
              flex: 1,
            }}
          >
            <table
              css={{
                width: '100%',
                minWidth: displayedColumns.reduce((sum, c) => sum + colWidth(c.key), 0),
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                whiteSpace: 'nowrap',
              }}
            >
              <colgroup>
                {displayedColumns.map((column) => (
                  <col key={column.key} style={{ width: colWidth(column.key) }} />
                ))}
              </colgroup>
              <thead ref={theadRef}>
                {/* Shared header (once for all properties), in three rows: the Soil/Zone group bands,
                    then the measure names, then the units. Zone/Probe is one tall cell spanning all
                    three; the bands tint their column cells to match. */}
                <tr ref={bandRowRef}>
                  <th
                    rowSpan={3}
                    css={{
                      ...headerCellCss,
                      ...labelCellCss,
                      // The corner cell: pinned on both axes. Spreading aboveScrollFadeCss here
                      // would reset position to relative and it would scroll off with the data
                      // columns.
                      ...stickyLabelCss(colors.white, !atLeft),
                      top: 0,
                      zIndex: STICKY_HEADER_CORNER_Z,
                      // Spreading headerCellCss then labelCellCss leaves only the label's rule, so
                      // the corner has to state both.
                      boxShadow: `${HEADER_BOTTOM_RULE}, ${LABEL_RIGHT_RULE}`,
                      verticalAlign: 'middle',
                    }}
                  >
                    {labelColumn && <ColumnHeader label={labelColumn.headerLabel} />}
                  </th>
                  {soilSpan > 0 && (
                    <th
                      css={{ ...groupBandCss, ...stickyHeaderCss(0), background: colors.grey50 }}
                      colSpan={soilSpan}
                    >
                      {translate.phrases.banyanApp('Soil Moisture Probe Data')}
                    </th>
                  )}
                  {zoneSpan > 0 && (
                    <th
                      css={{ ...groupBandCss, ...stickyHeaderCss(0), background: dashboardColors.zoneBandBg }}
                      colSpan={zoneSpan}
                    >
                      {translate.phrases.banyanApp('Zone Data')}
                    </th>
                  )}
                </tr>
                <tr ref={nameRowRef}>
                  {headerGroups.map((group) => {
                    const first = group.columns[0]

                    return (
                      <th
                        key={first.key}
                        colSpan={group.columns.length}
                        // Groups without units take this row and the unit row below, so the unit row
                        // only has to render cells for the columns that actually carry one.
                        rowSpan={group.hasUnits ? 1 : 2}
                        css={{
                          ...headerCellCss,
                          ...stickyHeaderCss(headerOffsets.names),
                          // Groups with a unit row below carry no rule of their own — the header's
                          // single line belongs at the bottom of the block, not between a measure's
                          // name and its own units.
                          ...(group.hasUnits ? { boxShadow: 'none' } : {}),
                          background:
                            first.group === 'soil' ? colors.white : dashboardColors.zoneColumnHeaderBg,
                        }}
                      >
                        <ColumnHeader label={first.headerLabel} period={first.headerPeriod} />
                      </th>
                    )
                  })}
                </tr>
                <tr>
                  {dataColumns
                    .filter((column) => column.headerUnit)
                    .map((column) => (
                      <th
                        key={column.key}
                        css={{
                          ...headerCellCss,
                          ...stickyHeaderCss(headerOffsets.units),
                          paddingTop: 0,
                          fontWeight: 500,
                          color: dashboardColors.subtleText,
                          background:
                            column.group === 'soil' ? colors.white : dashboardColors.zoneColumnHeaderBg,
                        }}
                      >
                        {column.headerUnit}
                      </th>
                    ))}
                </tr>
              </thead>
              {orderedSections.map((section, index) => (
                <PropertyBody
                  key={section.propertyId}
                  isFirst={index === 0}
                  section={section}
                  columns={displayedColumns}
                  error={error}
                  showStickyEdge={!atLeft}
                  stickyTop={headerOffsets.body}
                />
              ))}
            </table>
          </div>
          {/* Shown only for the side that actually has content hidden, so a table that fits its
              container carries neither. */}
          {!atRight && <div css={scrollFadeCss('right', 0)} />}
        </div>
      ) : (
        <div css={centeredMessageCss}>{translate.phrases.banyanApp('No data to display')}</div>
      )}

      <SoilChartModal probe={chartProbe} onClose={() => setChartProbe(null)} />
    </WidgetCard>
  )
}
